import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
    ChevronLeft, Save, Loader2, Plus, Trash2, GripVertical, ChevronUp, ChevronDown,
    Settings2, Eye, Copy, FileText, Video, Star, Type, Palette, X, Tag
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_TYPES = [
    { value: 'text', label: 'Short Text', icon: Type },
    { value: 'textarea', label: 'Long Text', icon: FileText },
    { value: 'rating', label: 'Star Rating', icon: Star },
    { value: 'video_recorder', label: 'Video Recording', icon: Video },
];

const INPUT = "w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20";
const LABEL = "block text-xs font-medium text-stone-600 mb-1";

function genId() { return 'fld_' + Math.random().toString(36).substr(2, 9); }
function genPageId() { return 'pg_' + Math.random().toString(36).substr(2, 9); }

function defaultField(type = 'textarea') {
    const labels = { text: 'Your Name', textarea: 'Your Testimonial', rating: 'Overall Rating', video_recorder: 'Record Video' };
    return {
        field_id: genId(),
        field_type: type,
        label: labels[type] || 'New Field',
        required: false,
        placeholder: '',
        options: { max_seconds: 30, show_teleprompter: true, max_stars: 5 },
        order: 0,
    };
}

function defaultPage(order = 0) {
    return {
        page_id: genPageId(),
        title: order === 0 ? 'Welcome' : `Page ${order + 1}`,
        description: '',
        order,
        fields: order === 0 ? [defaultField('textarea')] : [],
    };
}

/* ---- Field Options by type ---- */
function FieldOptions({ field, onUpdate }) {
    if (field.field_type === 'video_recorder') {
        return (
            <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                    <label className={LABEL}>Max Seconds</label>
                    <input type="number" value={field.options?.max_seconds || 30} min={5} max={120}
                        onChange={e => onUpdate({ options: { ...field.options, max_seconds: parseInt(e.target.value) || 30 } })}
                        className={INPUT} />
                </div>
                <div>
                    <label className={LABEL}>Show Teleprompter</label>
                    <label className="flex items-center gap-2 mt-1.5">
                        <input type="checkbox" checked={field.options?.show_teleprompter !== false}
                            onChange={e => onUpdate({ options: { ...field.options, show_teleprompter: e.target.checked } })}
                            className="rounded border-stone-300" />
                        <span className="text-xs text-stone-600">Show script overlay</span>
                    </label>
                </div>
            </div>
        );
    }
    if (field.field_type === 'rating') {
        return (
            <div className="mt-2">
                <label className={LABEL}>Max Stars</label>
                <input type="number" value={field.options?.max_stars || 5} min={3} max={10}
                    onChange={e => onUpdate({ options: { ...field.options, max_stars: parseInt(e.target.value) || 5 } })}
                    className={`${INPUT} w-24`} />
            </div>
        );
    }
    return null;
}

/* ---- Field Card ---- */
function FieldCard({ field, index, total, onUpdate, onRemove, onMoveUp, onMoveDown }) {
    const [expanded, setExpanded] = useState(false);
    const Icon = FIELD_TYPES.find(t => t.value === field.field_type)?.icon || Type;

    return (
        <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 flex items-center gap-2 bg-stone-50">
                <GripVertical className="w-3.5 h-3.5 text-stone-300 flex-shrink-0" />
                <Icon className="w-3.5 h-3.5 text-stone-400" />
                <input value={field.label} onChange={e => onUpdate({ label: e.target.value })}
                    className="flex-1 text-sm font-medium text-stone-800 bg-transparent border-none outline-none"
                    placeholder="Field label..." />
                <select value={field.field_type} onChange={e => onUpdate({ field_type: e.target.value })}
                    className="text-xs border border-stone-200 rounded px-1.5 py-1 bg-white">
                    {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <label className="flex items-center gap-1 text-xs text-stone-500">
                    <input type="checkbox" checked={field.required || false} onChange={e => onUpdate({ required: e.target.checked })}
                        className="rounded border-stone-300" /> Req
                </label>
                <button onClick={() => setExpanded(!expanded)} className="p-1 hover:bg-stone-200 rounded">
                    {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
                <button onClick={onRemove} className="p-1 hover:bg-red-100 rounded text-stone-400 hover:text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                </button>
            </div>
            {expanded && (
                <div className="px-3 py-3 border-t border-stone-100 space-y-2">
                    <div>
                        <label className={LABEL}>Placeholder</label>
                        <input value={field.placeholder || ''} onChange={e => onUpdate({ placeholder: e.target.value })}
                            className={INPUT} placeholder="Placeholder text..." />
                    </div>
                    <FieldOptions field={field} onUpdate={onUpdate} />
                    <div className="flex justify-end gap-1 pt-1">
                        <button onClick={onMoveUp} disabled={index === 0}
                            className="p-1 rounded hover:bg-stone-100 disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                        <button onClick={onMoveDown} disabled={index === total - 1}
                            className="p-1 rounded hover:bg-stone-100 disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ---- Tag Input (notify emails, auto tags) ---- */
function TagInput({ value = [], onChange, placeholder }) {
    const [input, setInput] = useState('');
    const add = () => {
        const trimmed = input.trim();
        if (trimmed && !value.includes(trimmed)) { onChange([...value, trimmed]); setInput(''); }
    };
    return (
        <div>
            <div className="flex flex-wrap gap-1 mb-1">
                {value.map((t, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-stone-100 text-xs text-stone-700">
                        {t}
                        <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="hover:text-red-500"><X className="w-3 h-3" /></button>
                    </span>
                ))}
            </div>
            <input value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                className={INPUT} placeholder={placeholder} />
        </div>
    );
}

/* ---- Preview Panel ---- */
function PreviewPanel({ pages, settings }) {
    const brandColor = settings?.branding?.brand_color || '#1c1917';
    return (
        <div className="max-w-md mx-auto bg-white rounded-xl shadow-lg overflow-hidden border">
            {pages.map((page, pi) => (
                <div key={page.page_id} className="p-6 border-b last:border-b-0">
                    <h3 className="font-semibold text-stone-900 mb-1">{page.title || `Page ${pi + 1}`}</h3>
                    {page.description && <p className="text-sm text-stone-500 mb-4">{page.description}</p>}
                    <div className="space-y-4">
                        {page.fields.map(f => (
                            <div key={f.field_id}>
                                <label className="block text-sm font-medium text-stone-700 mb-1">
                                    {f.label} {f.required && <span className="text-red-500">*</span>}
                                </label>
                                {f.field_type === 'text' && <input className={INPUT} placeholder={f.placeholder} disabled />}
                                {f.field_type === 'textarea' && <textarea className={INPUT} rows={3} placeholder={f.placeholder} disabled />}
                                {f.field_type === 'rating' && (
                                    <div className="flex gap-1">
                                        {Array.from({ length: f.options?.max_stars || 5 }).map((_, i) => (
                                            <Star key={i} className="w-6 h-6 text-amber-400" fill={i < 3 ? 'currentColor' : 'none'} />
                                        ))}
                                    </div>
                                )}
                                {f.field_type === 'video_recorder' && (
                                    <div className="bg-stone-900 rounded-lg aspect-video flex items-center justify-center">
                                        <div className="text-center text-white/60">
                                            <Video className="w-10 h-10 mx-auto mb-2" />
                                            <p className="text-sm">Video Recorder ({f.options?.max_seconds || 30}s max)</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
            <div className="p-6 bg-stone-50">
                <button className="w-full py-2.5 rounded-lg text-white text-sm font-medium"
                    style={{ backgroundColor: brandColor }}>Submit Testimonial</button>
            </div>
        </div>
    );
}

/* ======== Main Component ======== */
export default function TestimonialBuilder() {
    const { requestId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeader } = useAuth();
    const isNew = !requestId || requestId === 'new';

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [pages, setPages] = useState([defaultPage(0)]);
    const [settings, setSettings] = useState({
        confirmation_message: 'Thank you for your testimonial! We really appreciate your feedback.',
        redirect_url: '',
        notify_emails: [],
        auto_tags: [],
        branding: { brand_color: '#1c1917', logo_url: '', show_branding: true },
    });
    const [isTemplate, setIsTemplate] = useState(false);
    const [activeTab, setActiveTab] = useState('pages'); // pages | settings | preview
    const [activePage, setActivePage] = useState(0);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);

    /* Load existing */
    const load = useCallback(async () => {
        if (isNew) return;
        try {
            const res = await axios.get(`${API}/testimonials/${requestId}`, { headers: getAuthHeader() });
            const d = res.data;
            setName(d.name || '');
            setDescription(d.description || '');
            setPages(d.pages?.length ? d.pages : [defaultPage(0)]);
            setSettings({ ...settings, ...d.settings });
            setIsTemplate(d.is_template || false);
        } catch { toast.error('Failed to load testimonial request'); navigate('/testimonials'); }
        finally { setLoading(false); }
    }, [requestId, isNew]);

    useEffect(() => { load(); }, [load]);

    /* Save */
    const handleSave = async () => {
        if (!name.trim()) { toast.error('Name is required'); return; }
        setSaving(true);
        try {
            const body = { name, description, pages, settings, is_template: isTemplate };
            if (isNew) {
                const res = await axios.post(`${API}/testimonials`, body, { headers: getAuthHeader() });
                toast.success('Testimonial request created');
                navigate(`/testimonials/${res.data.request_id}/edit`, { replace: true });
            } else {
                await axios.put(`${API}/testimonials/${requestId}`, body, { headers: getAuthHeader() });
                toast.success('Saved');
            }
        } catch { toast.error('Failed to save'); }
        finally { setSaving(false); }
    };

    /* Page helpers */
    const addPage = () => {
        const newPage = defaultPage(pages.length);
        setPages([...pages, newPage]);
        setActivePage(pages.length);
    };

    const removePage = (idx) => {
        if (pages.length <= 1) { toast.error('Need at least one page'); return; }
        const next = pages.filter((_, i) => i !== idx).map((p, i) => ({ ...p, order: i }));
        setPages(next);
        setActivePage(Math.min(activePage, next.length - 1));
    };

    const updatePage = (idx, updates) => {
        setPages(pages.map((p, i) => i === idx ? { ...p, ...updates } : p));
    };

    const movePage = (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= pages.length) return;
        const next = [...pages];
        [next[idx], next[target]] = [next[target], next[idx]];
        setPages(next.map((p, i) => ({ ...p, order: i })));
        setActivePage(target);
    };

    /* Field helpers for current page */
    const currentPage = pages[activePage] || pages[0];

    const addField = (type) => {
        const field = defaultField(type);
        field.order = currentPage.fields.length;
        const updated = { ...currentPage, fields: [...currentPage.fields, field] };
        updatePage(activePage, updated);
    };

    const updateField = (fieldIdx, updates) => {
        const fields = currentPage.fields.map((f, i) => i === fieldIdx ? { ...f, ...updates } : f);
        updatePage(activePage, { fields });
    };

    const removeField = (fieldIdx) => {
        const fields = currentPage.fields.filter((_, i) => i !== fieldIdx).map((f, i) => ({ ...f, order: i }));
        updatePage(activePage, { fields });
    };

    const moveField = (fieldIdx, dir) => {
        const target = fieldIdx + dir;
        if (target < 0 || target >= currentPage.fields.length) return;
        const fields = [...currentPage.fields];
        [fields[fieldIdx], fields[target]] = [fields[target], fields[fieldIdx]];
        updatePage(activePage, { fields: fields.map((f, i) => ({ ...f, order: i })) });
    };

    if (loading) return (
        <div className="flex items-center justify-center h-96">
            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
        </div>
    );

    const tabs = [
        { key: 'pages', label: 'Pages', icon: FileText },
        { key: 'settings', label: 'Settings', icon: Settings2 },
        { key: 'preview', label: 'Preview', icon: Eye },
    ];

    return (
        <div className="h-full flex flex-col">
            {/* Top bar */}
            <div className="flex items-center justify-between px-6 py-3 border-b border-stone-200 bg-white">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/testimonials')} className="p-1.5 rounded-lg hover:bg-stone-100">
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <input value={name} onChange={e => setName(e.target.value)}
                            className="text-lg font-semibold text-stone-900 bg-transparent border-none outline-none w-64"
                            placeholder="Testimonial Request Name..." />
                        <input value={description} onChange={e => setDescription(e.target.value)}
                            className="block text-xs text-stone-400 bg-transparent border-none outline-none w-80 mt-0.5"
                            placeholder="Description (optional)" />
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1.5 text-xs text-stone-500 mr-3">
                        <input type="checkbox" checked={isTemplate} onChange={e => setIsTemplate(e.target.checked)}
                            className="rounded border-stone-300" /> Template
                    </label>
                    <Button onClick={handleSave} disabled={saving} size="sm">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                        Save
                    </Button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 px-6 pt-3 bg-white border-b border-stone-200">
                {tabs.map(t => (
                    <button key={t.key} onClick={() => setActiveTab(t.key)}
                        className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-t-lg border-b-2 transition-colors ${
                            activeTab === t.key
                                ? 'border-stone-900 text-stone-900 font-medium'
                                : 'border-transparent text-stone-500 hover:text-stone-700'
                        }`}>
                        <t.icon className="w-3.5 h-3.5" /> {t.label}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto bg-[#FAFAF5]">
                {activeTab === 'pages' && (
                    <div className="flex h-full">
                        {/* Page list sidebar */}
                        <div className="w-56 border-r border-stone-200 bg-white p-3 flex flex-col gap-1 overflow-y-auto">
                            <div className="text-xs font-medium text-stone-500 uppercase tracking-wider mb-2 px-1">Pages</div>
                            {pages.map((p, i) => (
                                <div key={p.page_id}
                                    onClick={() => setActivePage(i)}
                                    className={`group flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer text-sm transition-colors ${
                                        activePage === i ? 'bg-stone-900 text-white' : 'hover:bg-stone-100 text-stone-700'
                                    }`}>
                                    <span className={`w-5 h-5 flex items-center justify-center rounded text-xs font-medium ${
                                        activePage === i ? 'bg-white/20' : 'bg-stone-100'
                                    }`}>{i + 1}</span>
                                    <span className="flex-1 truncate">{p.title || `Page ${i + 1}`}</span>
                                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100">
                                        <button onClick={e => { e.stopPropagation(); movePage(i, -1); }}
                                            className={`p-0.5 rounded ${activePage === i ? 'hover:bg-white/20' : 'hover:bg-stone-200'}`}>
                                            <ChevronUp className="w-3 h-3" />
                                        </button>
                                        <button onClick={e => { e.stopPropagation(); movePage(i, 1); }}
                                            className={`p-0.5 rounded ${activePage === i ? 'hover:bg-white/20' : 'hover:bg-stone-200'}`}>
                                            <ChevronDown className="w-3 h-3" />
                                        </button>
                                        {pages.length > 1 && (
                                            <button onClick={e => { e.stopPropagation(); removePage(i); }}
                                                className={`p-0.5 rounded ${activePage === i ? 'hover:bg-white/20 text-red-300' : 'hover:bg-red-100 text-red-400'}`}>
                                                <Trash2 className="w-3 h-3" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                            <button onClick={addPage}
                                className="flex items-center gap-1.5 px-2.5 py-2 text-sm text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded-lg mt-1">
                                <Plus className="w-3.5 h-3.5" /> Add Page
                            </button>
                        </div>

                        {/* Page editor */}
                        <div className="flex-1 p-6 overflow-y-auto">
                            <div className="max-w-2xl mx-auto space-y-4">
                                {/* Page title & description */}
                                <div className="bg-white rounded-lg border border-stone-200 p-4 space-y-3">
                                    <div>
                                        <label className={LABEL}>Page Title</label>
                                        <input value={currentPage.title} onChange={e => updatePage(activePage, { title: e.target.value })}
                                            className={INPUT} placeholder="Page title..." />
                                    </div>
                                    <div>
                                        <label className={LABEL}>Page Description</label>
                                        <textarea value={currentPage.description || ''} onChange={e => updatePage(activePage, { description: e.target.value })}
                                            className={INPUT} rows={2} placeholder="Optional description shown above the fields..." />
                                    </div>
                                </div>

                                {/* Fields */}
                                <div className="space-y-2">
                                    <div className="text-xs font-medium text-stone-500 uppercase tracking-wider">Fields</div>
                                    {currentPage.fields.length === 0 && (
                                        <div className="text-center py-8 text-stone-400 text-sm border-2 border-dashed border-stone-200 rounded-lg">
                                            No fields yet. Add one below.
                                        </div>
                                    )}
                                    {currentPage.fields.map((f, i) => (
                                        <FieldCard key={f.field_id} field={f} index={i} total={currentPage.fields.length}
                                            onUpdate={u => updateField(i, u)} onRemove={() => removeField(i)}
                                            onMoveUp={() => moveField(i, -1)} onMoveDown={() => moveField(i, 1)} />
                                    ))}
                                </div>

                                {/* Add field buttons */}
                                <div className="flex flex-wrap gap-2">
                                    {FIELD_TYPES.map(t => (
                                        <button key={t.value} onClick={() => addField(t.value)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-stone-200 rounded-lg bg-white hover:bg-stone-50 text-stone-600 hover:text-stone-800 transition-colors">
                                            <t.icon className="w-3.5 h-3.5" /> {t.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'settings' && (
                    <div className="max-w-xl mx-auto p-6 space-y-6">
                        {/* Confirmation */}
                        <div className="bg-white rounded-lg border border-stone-200 p-4 space-y-3">
                            <h3 className="text-sm font-semibold text-stone-800">After Submission</h3>
                            <div>
                                <label className={LABEL}>Confirmation Message</label>
                                <textarea value={settings.confirmation_message}
                                    onChange={e => setSettings({ ...settings, confirmation_message: e.target.value })}
                                    className={INPUT} rows={2} />
                            </div>
                            <div>
                                <label className={LABEL}>Redirect URL (optional)</label>
                                <input value={settings.redirect_url || ''} onChange={e => setSettings({ ...settings, redirect_url: e.target.value })}
                                    className={INPUT} placeholder="https://..." />
                            </div>
                        </div>

                        {/* Notifications */}
                        <div className="bg-white rounded-lg border border-stone-200 p-4 space-y-3">
                            <h3 className="text-sm font-semibold text-stone-800">Notifications</h3>
                            <div>
                                <label className={LABEL}>Notify Emails (press Enter to add)</label>
                                <TagInput value={settings.notify_emails} onChange={v => setSettings({ ...settings, notify_emails: v })}
                                    placeholder="email@example.com" />
                            </div>
                        </div>

                        {/* Tags */}
                        <div className="bg-white rounded-lg border border-stone-200 p-4 space-y-3">
                            <h3 className="text-sm font-semibold text-stone-800">Auto Tags</h3>
                            <div>
                                <label className={LABEL}>Tags applied to contact on submission</label>
                                <TagInput value={settings.auto_tags} onChange={v => setSettings({ ...settings, auto_tags: v })}
                                    placeholder="testimonial, video-review" />
                            </div>
                        </div>

                        {/* Branding */}
                        <div className="bg-white rounded-lg border border-stone-200 p-4 space-y-3">
                            <h3 className="text-sm font-semibold text-stone-800 flex items-center gap-1.5">
                                <Palette className="w-4 h-4" /> Branding
                            </h3>
                            <div className="flex items-center gap-3">
                                <label className={LABEL}>Brand Color</label>
                                <input type="color" value={settings.branding?.brand_color || '#1c1917'}
                                    onChange={e => setSettings({ ...settings, branding: { ...settings.branding, brand_color: e.target.value } })}
                                    className="w-8 h-8 rounded cursor-pointer border border-stone-200" />
                                <input value={settings.branding?.brand_color || '#1c1917'}
                                    onChange={e => setSettings({ ...settings, branding: { ...settings.branding, brand_color: e.target.value } })}
                                    className={`${INPUT} w-28`} />
                            </div>
                            <div>
                                <label className={LABEL}>Logo URL (optional)</label>
                                <input value={settings.branding?.logo_url || ''} onChange={e => setSettings({ ...settings, branding: { ...settings.branding, logo_url: e.target.value } })}
                                    className={INPUT} placeholder="https://..." />
                            </div>
                            <label className="flex items-center gap-2">
                                <input type="checkbox" checked={settings.branding?.show_branding !== false}
                                    onChange={e => setSettings({ ...settings, branding: { ...settings.branding, show_branding: e.target.checked } })}
                                    className="rounded border-stone-300" />
                                <span className="text-sm text-stone-600">Show "Powered by" branding</span>
                            </label>
                        </div>
                    </div>
                )}

                {activeTab === 'preview' && (
                    <div className="p-6 overflow-y-auto">
                        <div className="text-center text-xs text-stone-400 mb-4">Mobile Preview (480px)</div>
                        <PreviewPanel pages={pages} settings={settings} />
                    </div>
                )}
            </div>
        </div>
    );
}
